// CTenpaiBanner — 宣告「聽」時的橫幅動畫（比照原版 ListenAnimEntity）
// 一整塊橫幅 = 橘色速度線底圖 + 中央一張隨機臉 + 3 顆各自脈動的星，放在一個 Container 裡，
// 以 Container 中心為錨點整體移動 / 縮放：
//   1. 從畫面左外滑入到水平置中、同時垂直展開（scaleY 0→1）
//   2. 停留約 1 秒
//   3. 整塊「飛向宣告聽牌那家的座位」並縮成 0 消失（依座位方向不同 → 由 caller 傳入目標座標）
//
// === 使用圖片 ===
// - public/assets/ui/tenpai-banner.png（key: TENPAI_BANNER_KEY，1280×108 橘色速度線）— 底圖
// - public/assets/ui/tenpai-face.png（key: TENPAI_FACE_KEY，576×108 ×2 frame）— 中央隨機臉
// - public/assets/ui/tenpai-star.png（key: TENPAI_STAR_KEY，54×54 ×12 frame）— 3 顆脈動星（取 frame 0）

import type Phaser from 'phaser';

import { SCREEN_CENTER_X, SCREEN_CENTER_Y } from '@config/game-config';

import { killAndDestroyContainer } from './tween-helpers';
import { UIDepth } from './ui-depths';
import { TENPAI_BANNER_KEY, TENPAI_FACE_KEY, TENPAI_STAR_KEY } from '@scenes/boot-scene';

// ============================================================================
// 版面 / 動畫常數
// ============================================================================
const BANNER_W = 1280; // 底圖原寬（= 邏輯畫面寬，置中時鋪滿）
const BANNER_DEPTH = UIDepth.TenpaiBanner; // 蓋過所有 UI（actionIcon 80 / 手勢 70）

const CENTER_X = SCREEN_CENTER_X; // 置中時 Container 中心 X
const CENTER_Y = SCREEN_CENTER_Y; // 垂直置中
const OFFSCREEN_X = CENTER_X - BANNER_W; // 起點：整條完全在畫面左外（中心 = -640）

const FLY_IN_MS = 200; // 滑入 + 垂直展開
const HOLD_MS = 1000; // 停留
const FLY_OUT_MS = 200; // 飛向座位 + 縮小消失

const FACE_FRAME_COUNT = 2; // 隨機臉 frame 數（0 / 1）

// 3 顆脈動星：相對 Container 中心的位置（px）+ 脈動縮放（from↔to yoyo）+ 單程時長
// 數值比照原版 STAR_POS / STAR_SCALE_FROM / STAR_SCALE_TO / STAR_SHINE_DURATION（dip→px 還原）
const STARS: ReadonlyArray<{ x: number; y: number; from: number; to: number; durationMs: number }> = [
  { x: -190, y: -28, from: 0.6, to: 1.1, durationMs: 500 },
  { x: -300, y: 24, from: 0.9, to: 0.4, durationMs: 300 },
  { x: -40, y: -10, from: 1.2, to: 0.7, durationMs: 400 },
];

export type TTenpaiBannerTarget = { readonly x: number; readonly y: number };

export class CTenpaiBanner {
  private container: Phaser.GameObjects.Container | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  isPlaying(): boolean {
    return this.container !== null;
  }

  /**
   * 播放整段：左滑入展開 → 停留 → 飛向 target（宣告聽牌那家座位）縮小消失。
   * @param target 飛去的目標中心座標（通常是該家風字徽章座標 SEAT_BADGE_SPOTS[seat]）
   */
  play(target: TTenpaiBannerTarget, onComplete?: () => void): void {
    this.destroy();

    const bg = this.scene.add.image(0, 0, TENPAI_BANNER_KEY);
    const faceFrame = Math.floor(Math.random() * FACE_FRAME_COUNT);
    const face = this.scene.add.image(0, 0, TENPAI_FACE_KEY, faceFrame);

    const container = this.scene.add
      .container(OFFSCREEN_X, CENTER_Y, [bg, face])
      .setDepth(BANNER_DEPTH);
    container.scaleY = 0; // 起始：垂直收合（展開靠 scaleY 0→1）
    this.container = container;

    // 3 顆脈動星：各自 yoyo 循環縮放
    for (const s of STARS) {
      const star = this.scene.add.image(s.x, s.y, TENPAI_STAR_KEY, 0).setScale(s.from);
      container.add(star);
      this.scene.tweens.add({
        targets: star,
        scaleX: s.to,
        scaleY: s.to,
        duration: s.durationMs,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }

    // Phase 1：滑入水平置中 + 垂直展開
    this.scene.tweens.add({
      targets: container,
      x: CENTER_X,
      scaleY: 1,
      duration: FLY_IN_MS,
      ease: 'Quad.easeOut',
      onComplete: () => this.scene.time.delayedCall(HOLD_MS, () => this.flyToSeat(target, onComplete)),
    });
  }

  /** Phase 3：整塊飛向座位、同時縮成 0，結束銷毀。 */
  private flyToSeat(target: TTenpaiBannerTarget, onComplete?: () => void): void {
    const container = this.container;
    if (!container) return;
    this.scene.tweens.add({
      targets: container,
      x: target.x,
      y: target.y,
      scaleX: 0,
      scaleY: 0,
      duration: FLY_OUT_MS,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.destroy();
        onComplete?.();
      },
    });
  }

  /** 立即銷毀（回合結束 / 中斷）。 */
  destroy(): void {
    killAndDestroyContainer(this.scene, this.container);
    this.container = null;
  }
}
